import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { financeService } from '@/services/financeService'
import {
  MVP_FORM_FIELDS,
  TYPES_WITH_EMPRESA_PREFILL,
  isMvpType,
  type FinanceFormField,
  type PortalTypeKey,
} from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import {
  EXTENDED_FORM_FIELDS,
  EXTENDED_TYPES_WITH_EMPRESA_PREFILL,
} from '@/components/Financeiro/solicitacoes/financeSolicitacoesExtended'
import { FinanceFormEmpresaSection } from '@/components/Financeiro/solicitacoes/FinanceFormEmpresaSection'
import {
  FinanceRequestFormField,
  isFinanceFieldFullWidth,
} from '@/components/Financeiro/solicitacoes/FinanceRequestFormField'
import type { FinanceTypeCard } from '@/hooks/useFinanceiroSolicitacoesPage'
import { cn } from '@/utils/cn'
import {
  applyMapaCargoToFormValues,
  getMapaCargoById,
} from '@/components/Configuracao/mapaCargos/mapaCargosUtils'
import { useMapaCargosOverrides } from '@/hooks/useMapaCargosOverrides'

type Props = {
  type: FinanceTypeCard
  open: boolean
  submitting: boolean
  hideEmpresaSearch?: boolean
  presentation?: 'crm' | 'portal'
  onClose: () => void
  onSubmit: (
    formData: Record<string, string>,
    linkedEmpresaId?: string,
    linkedDealId?: string,
    attachments?: string[],
  ) => void
}

function resolveFields(typeKey: string): FinanceFormField[] {
  if (isMvpType(typeKey)) return MVP_FORM_FIELDS[typeKey]
  if (typeKey in EXTENDED_FORM_FIELDS) {
    return EXTENDED_FORM_FIELDS[typeKey as keyof typeof EXTENDED_FORM_FIELDS]
  }
  return []
}

function usesEmpresaPrefill(typeKey: string): boolean {
  if (isMvpType(typeKey)) return TYPES_WITH_EMPRESA_PREFILL.includes(typeKey)
  return (EXTENDED_TYPES_WITH_EMPRESA_PREFILL as readonly string[]).includes(typeKey)
}

function validateDescansoParceiro(values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {}
  const meses = Number(values.mesesParceria)
  const nfs = Number(values.nfsEmitidas)
  const dias = Number(values.diasCorridos)

  if (values.dataInicioPausa && values.dataRetornoPrevisto && values.dataInicioPausa > values.dataRetornoPrevisto) {
    errors.dataRetornoPrevisto = 'Data de retorno deve ser após o início da pausa'
  }

  if (!Number.isFinite(meses) || meses < 12) {
    errors.mesesParceria = 'Descanso exige pelo menos 12 meses de parceria'
  }
  if (!Number.isFinite(nfs) || nfs < 12) {
    errors.nfsEmitidas = 'Descanso exige pelo menos 12 NFs emitidas'
  }
  if (!Number.isFinite(dias) || dias <= 0 || dias > 20) {
    errors.diasCorridos = 'Informe entre 1 e 20 dias corridos de descanso'
  }
  if (values.declaracaoCiente !== 'ciente') {
    errors.declaracaoCiente = 'É necessário declarar ciência do acordo de descanso'
  }

  return errors
}

function validateViagem(values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {}
  if (values.dataIda && values.dataVolta && values.dataIda > values.dataVolta) {
    errors.dataVolta = 'Data de volta deve ser após a ida'
  }
  return errors
}

const FORM_ID = 'finance-request-form'

export function FinanceRequestFormModal({
  type,
  open,
  submitting,
  hideEmpresaSearch = false,
  presentation = 'crm',
  onClose,
  onSubmit,
}: Props) {
  const fields = resolveFields(type.key)
  const showEmpresaSearch = !hideEmpresaSearch && usesEmpresaPrefill(type.key)
  const [values, setValues] = useState<Record<string, string>>({})
  const [files, setFiles] = useState<Record<string, File[]>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [linkedEmpresaId, setLinkedEmpresaId] = useState<string | undefined>()
  const [linkedDealId, setLinkedDealId] = useState<string | undefined>()
  const [uploading, setUploading] = useState(false)
  const { overrides: mapaCargoOverrides } = useMapaCargosOverrides()

  useEffect(() => {
    if (open) {
      setValues({})
      setFiles({})
      setErrors({})
      setLinkedEmpresaId(undefined)
      setLinkedDealId(undefined)
      setUploading(false)
    }
  }, [open, type.key])

  const busy = submitting || uploading

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const nextErrors: Record<string, string> = {}

    for (const field of fields) {
      if (field.name === 'linkedDealId') continue
      if (field.type === 'file') {
        const list = files[field.name] ?? []
        if (field.required && list.length === 0) {
          nextErrors[field.name] = 'Anexe pelo menos um arquivo'
        }
        continue
      }
      if (field.required && !values[field.name]?.trim()) {
        nextErrors[field.name] = 'Campo obrigatório'
      }
    }

    if (type.key === 'P3_ENTRADA_PARCEIRO') {
      Object.assign(nextErrors, validateDescansoParceiro(values))
    }
    if (type.key === 'P1_ENTRADA_CONTRATO' || type.key === 'G1_ENTRADA_CONTRATO') {
      Object.assign(nextErrors, validateViagem(values))
    }

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    setUploading(true)
    try {
      const attachmentUrls: string[] = []
      const formData: Record<string, string> = { ...values }

      if (type.key === 'G5_CONTRATACAO_PJ') {
        const cargo = getMapaCargoById(formData.mapaCargoId, mapaCargoOverrides)
        if (cargo) {
          formData.funcaoCargo = cargo.titulo
        }
      }

      for (const field of fields) {
        if (field.type !== 'file') continue
        const list = files[field.name] ?? []
        const names: string[] = []
        for (const file of list) {
          const uploaded = await financeService.uploadRequestAttachment(file)
          attachmentUrls.push(uploaded.fileUrl)
          names.push(uploaded.fileName)
        }
        if (names.length > 0) formData[field.name] = names.join(', ')
      }

      const manualDealId = formData.linkedDealId
      delete formData.linkedDealId
      onSubmit(
        formData,
        linkedEmpresaId,
        (linkedDealId ?? manualDealId?.trim()) || undefined,
        attachmentUrls,
      )
    } catch {
      setErrors({ _form: 'Falha ao enviar arquivo. Tente novamente.' })
    } finally {
      setUploading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='2xl'
      title={type.label}
      description={`SLA ${type.slaHours}h · protocolo gerado ao enviar`}
      footer={
        <>
          <Button type='button' variant='secondary' onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type='submit' form={FORM_ID} disabled={busy}>
            {uploading ? 'Enviando arquivos…' : submitting ? 'Abrindo chamado…' : 'Enviar solicitação'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={(e) => void handleSubmit(e)} className='space-y-6'>
        <div className='flex items-center gap-3 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-4 py-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-primary-muted)]'>
            <FileText size={18} className='text-[var(--brand-primary)]' />
          </div>
          <p className='text-[var(--font-color-muted)]' style={{ fontSize: 'var(--font-size-sm)' }}>
            Preencha os campos abaixo. Campos com * são obrigatórios.
          </p>
        </div>

        {errors._form && (
          <div className='rounded-lg border border-[#e5484d]/30 bg-[#e5484d]/5 px-4 py-3 text-sm text-[#e5484d]'>
            {errors._form}
          </div>
        )}

        {showEmpresaSearch && (
          <section className='space-y-3'>
            <h4
              className='font-medium uppercase tracking-wider text-[var(--font-color-label)]'
              style={{ fontSize: 'var(--font-size-label)' }}
            >
              Empresa vinculada
            </h4>
            <FinanceFormEmpresaSection
              variant={presentation === 'portal' ? 'portalLight' : 'default'}
              onSelect={(empresa, prefill, dealId) => {
                setLinkedEmpresaId(empresa.id)
                if (dealId) setLinkedDealId(dealId)
                setValues((v) => ({ ...v, ...prefill }))
              }}
              onClear={() => {
                setLinkedEmpresaId(undefined)
                setLinkedDealId(undefined)
              }}
            />
          </section>
        )}

        <section className='space-y-4'>
          <h4
            className='font-medium uppercase tracking-wider text-[var(--font-color-label)]'
            style={{ fontSize: 'var(--font-size-label)' }}
          >
            Dados da solicitação
          </h4>
          <div className='grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2'>
            {fields.map((field) => (
              <div
                key={field.name}
                className={cn(isFinanceFieldFullWidth(field) && 'sm:col-span-2')}
              >
                <FinanceRequestFormField
                  field={field}
                  value={values[field.name] ?? ''}
                  files={files[field.name] ?? []}
                  error={errors[field.name]}
                  onValueChange={(next) => {
                    if (type.key === 'G5_CONTRATACAO_PJ' && field.name === 'mapaCargoId') {
                      setValues((v) => applyMapaCargoToFormValues(next, v, mapaCargoOverrides))
                      if (next.trim()) setErrors((prev) => ({ ...prev, mapaCargoId: '', descricaoEscopo: '' }))
                      return
                    }
                    setValues((v) => ({ ...v, [field.name]: next }))
                    if (next.trim()) setErrors((prev) => ({ ...prev, [field.name]: '' }))
                  }}
                  onFilesChange={(picked) => {
                    setFiles((prev) => ({ ...prev, [field.name]: picked }))
                    if (picked.length > 0) setErrors((prev) => ({ ...prev, [field.name]: '' }))
                  }}
                />
              </div>
            ))}
          </div>
        </section>
      </form>
    </Modal>
  )
}

export type { PortalTypeKey }
